import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AppService from "../services/AppService";
import GooglePlayReleaseNotesService from "../services/GooglePlayReleaseNotesService";
import { useAuth } from "../hooks/useAuth";
import type { App } from "../types/app";
import type {
  GooglePlayReleaseNotesReport,
  GooglePlayReleaseNotesTemplateKey,
} from "../types/googlePlayReleaseNotes";
import {
  GOOGLE_PLAY_STORE_LOCALES,
  getStoreLocaleDisplayLabel,
} from "../utils/storeLocales";

const GOOGLE_PLAY_LOCALE_CODE_SET = new Set(
  GOOGLE_PLAY_STORE_LOCALES.map((locale) => locale.code.toLowerCase()),
);

const TEMPLATE_OPTIONS: Array<{
  key: GooglePlayReleaseNotesTemplateKey;
  label: string;
  description: string;
}> = [
  {
    key: "bug_fixes_improvements",
    label: "Bug fixes and improvements",
    description: "Safe default for routine updates and quality releases.",
  },
  {
    key: "new_features",
    label: "New features",
    description: "Use when the release includes user-facing updates.",
  },
  {
    key: "custom",
    label: "Custom text",
    description: "Write your own base note and localize it to selected locales.",
  },
];

const formatDateHeading = (value: Date) =>
  value.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const formatDateTime = (value: Date) =>
  value.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const GooglePlayReleaseNotesPage: React.FC = () => {
  const { user } = useAuth();
  const [apps, setApps] = React.useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = React.useState("");
  const [templateKey, setTemplateKey] =
    React.useState<GooglePlayReleaseNotesTemplateKey>(
      "bug_fixes_improvements",
    );
  const [customText, setCustomText] = React.useState("");
  const [selectedLocales, setSelectedLocales] = React.useState<string[]>([
    "en-US",
  ]);
  const [localeSearch, setLocaleSearch] = React.useState("");
  const [loadingApps, setLoadingApps] = React.useState(false);
  const [loadingReports, setLoadingReports] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [reports, setReports] = React.useState<GooglePlayReleaseNotesReport[]>([]);
  const [activeTab, setActiveTab] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [copiedKey, setCopiedKey] = React.useState<string | null>(null);
  const [expandedDateGroups, setExpandedDateGroups] = React.useState<
    Record<string, boolean>
  >({});

  const selectedApp = React.useMemo(
    () => apps.find((app) => app.appId === selectedAppId) || null,
    [apps, selectedAppId],
  );

  const groupedReports = React.useMemo(() => {
    const groups = reports.reduce<
      Array<{
        key: string;
        label: string;
        reports: GooglePlayReleaseNotesReport[];
      }>
    >((acc, report) => {
      const dateKey = report.generatedAt.toISOString().slice(0, 10);
      const existing = acc.find((item) => item.key === dateKey);
      if (existing) {
        existing.reports.push(report);
        return acc;
      }
      acc.push({
        key: dateKey,
        label: formatDateHeading(report.generatedAt),
        reports: [report],
      });
      return acc;
    }, []);

    return groups.sort((left, right) => right.key.localeCompare(left.key));
  }, [reports]);

  const filteredLocales = React.useMemo(() => {
    const query = localeSearch.trim().toLowerCase();
    if (!query) return GOOGLE_PLAY_STORE_LOCALES;
    return GOOGLE_PLAY_STORE_LOCALES.filter((locale) => {
      const label = getStoreLocaleDisplayLabel(locale.code).toLowerCase();
      return (
        locale.code.toLowerCase().includes(query) ||
        label.includes(query) ||
        locale.name.toLowerCase().includes(query)
      );
    });
  }, [localeSearch]);

  React.useEffect(() => {
    setSelectedLocales((prev) =>
      prev.filter((locale) =>
        GOOGLE_PLAY_LOCALE_CODE_SET.has(locale.toLowerCase()),
      ),
    );
  }, []);

  React.useEffect(() => {
    const loadApps = async () => {
      if (!user?.email) return;
      try {
        setLoadingApps(true);
        const userApps = await AppService.getUserApps(user.email);
        setApps(userApps);
        if (userApps.length > 0 && !selectedAppId) {
          setSelectedAppId(userApps[0].appId);
        }
      } catch (loadError) {
        console.error(loadError);
        setError("Failed to load registered apps.");
      } finally {
        setLoadingApps(false);
      }
    };
    loadApps();
  }, [selectedAppId, user?.email]);

  const loadReports = React.useCallback(async () => {
    if (!user?.email || !selectedAppId) {
      setReports([]);
      return;
    }
    try {
      setLoadingReports(true);
      const loaded = await GooglePlayReleaseNotesService.getReportsByApp({
        userId: user.email,
        appId: selectedAppId,
      });
      setReports(loaded);
      if (loaded.length > 0) {
        const latestDateKey = loaded[0].generatedAt.toISOString().slice(0, 10);
        setExpandedDateGroups((prev) => ({ ...prev, [latestDateKey]: true }));
      }
    } catch (loadError) {
      console.error(loadError);
      setError("Failed to load release note reports.");
    } finally {
      setLoadingReports(false);
    }
  }, [selectedAppId, user?.email]);

  React.useEffect(() => {
    loadReports();
  }, [loadReports]);

  const copyText = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(null), 1200);
    } catch (copyError) {
      console.error(copyError);
      setError("Failed to copy text.");
    }
  };

  const handleGenerate = async () => {
    if (!user?.email) return;
    if (!selectedApp) {
      setError("Select a registered app.");
      return;
    }
    if (selectedLocales.length === 0) {
      setError("Select at least one Google Play locale.");
      return;
    }
    if (templateKey === "custom" && !String(customText || "").trim()) {
      setError("Enter custom release note text.");
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      setSuccess(null);
      await GooglePlayReleaseNotesService.generateAndSaveReport({
        userId: user.email,
        app: selectedApp,
        locales: selectedLocales,
        templateKey,
        customText,
      });
      await loadReports();
      setActiveTab(1);
      setSuccess(
        `Generated and saved release notes for ${selectedLocales.length} locale${
          selectedLocales.length === 1 ? "" : "s"
        }.`,
      );
    } catch (generateError) {
      console.error(generateError);
      setError(
        generateError instanceof Error
          ? generateError.message
          : "Failed to generate release notes.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleLocaleToggle = (locale: string) => {
    setSelectedLocales((prev) =>
      prev.includes(locale)
        ? prev.filter((item) => item !== locale)
        : [...prev, locale],
    );
  };

  return (
    <Box sx={{ maxWidth: 1320, mx: "auto", px: 1.5 }}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(145deg, rgba(232,245,255,0.7) 0%, rgba(242,250,255,0.8) 40%, rgba(250,255,250,0.82) 100%)",
          mb: 2.5,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <AutoAwesomeIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Google Play Release Notes
            </Typography>
          </Box>
          <Typography color="text.secondary">
            Generate localized Google Play release notes from a template or custom
            base text, then save and manage reports by app.
          </Typography>
        </CardContent>
      </Card>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mb: 2 }}
      >
        <Tab label="Generate" />
        <Tab label="Reports" />
      </Tabs>

      {activeTab === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loadingApps || generating}>
                  <InputLabel>Registered app</InputLabel>
                  <Select
                    value={selectedAppId}
                    label="Registered app"
                    onChange={(event) => setSelectedAppId(event.target.value)}
                  >
                    {apps.map((app) => (
                      <MenuItem key={app.appId} value={app.appId}>
                        {app.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={generating}>
                  <InputLabel>Release note template</InputLabel>
                  <Select
                    value={templateKey}
                    label="Release note template"
                    onChange={(event) =>
                      setTemplateKey(
                        event.target.value as GooglePlayReleaseNotesTemplateKey,
                      )
                    }
                  >
                    {TEMPLATE_OPTIONS.map((template) => (
                      <MenuItem key={template.key} value={template.key}>
                        {template.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  {
                    TEMPLATE_OPTIONS.find((item) => item.key === templateKey)
                      ?.description
                  }
                </Typography>
              </Grid>

              {templateKey === "custom" ? (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Custom release note base text"
                    placeholder="Example: Bug fixes and performance improvements."
                    value={customText}
                    disabled={generating}
                    onChange={(event) => setCustomText(event.target.value)}
                  />
                </Grid>
              ) : null}

              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2.5,
                    p: 2,
                    bgcolor: "rgba(255,255,255,0.75)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Target Google Play locales
                    </Typography>
                    <Chip
                      size="small"
                      color="primary"
                      label={`${selectedLocales.length} selected`}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={localeSearch}
                      disabled={generating}
                      onChange={(event) => setLocaleSearch(event.target.value)}
                      label="Search locales"
                      placeholder="Search by code or language (e.g. en, arabic, pt-BR)"
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={generating}
                      onClick={() =>
                        setSelectedLocales(
                          Array.from(
                            new Set([
                              ...selectedLocales,
                              ...filteredLocales.map((locale) => locale.code),
                            ]),
                          ),
                        )
                      }
                    >
                      Select filtered
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      disabled={generating}
                      onClick={() => setSelectedLocales([])}
                    >
                      Clear
                    </Button>
                  </Box>
                  {filteredLocales.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      No locales match your search.
                    </Alert>
                  ) : null}
                  <Grid container spacing={0.75}>
                    {filteredLocales.map((locale) => {
                      const selected = selectedLocales.includes(locale.code);
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={locale.code}>
                          <Button
                            fullWidth
                            size="small"
                            variant={selected ? "contained" : "outlined"}
                            color={selected ? "primary" : "inherit"}
                            onClick={() => handleLocaleToggle(locale.code)}
                            disabled={generating}
                            sx={{
                              justifyContent: "flex-start",
                              textTransform: "none",
                              borderRadius: 1.5,
                              px: 1.1,
                            }}
                          >
                            {locale.flag} {getStoreLocaleDisplayLabel(locale.code)}
                          </Button>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={generating || loadingApps || !selectedAppId}
                    onClick={handleGenerate}
                  >
                    {generating ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Generate Release Notes"
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Saved Reports {selectedApp ? `for ${selectedApp.name}` : ""}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={loadingReports || !selectedAppId}
                onClick={loadReports}
              >
                Refresh
              </Button>
            </Box>
            <Grid container spacing={2} sx={{ mb: 1.5 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loadingApps || loadingReports}>
                  <InputLabel>Registered app</InputLabel>
                  <Select
                    value={selectedAppId}
                    label="Registered app"
                    onChange={(event) => setSelectedAppId(event.target.value)}
                  >
                    {apps.map((app) => (
                      <MenuItem key={app.appId} value={app.appId}>
                        {app.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {!selectedAppId ? (
              <Alert severity="info">Select a registered app to view reports.</Alert>
            ) : loadingReports ? (
              <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : groupedReports.length === 0 ? (
              <Alert severity="info">
                No release note reports saved yet for this app.
              </Alert>
            ) : (
              groupedReports.map((group) => (
                <Accordion
                  key={group.key}
                  disableGutters
                  expanded={Boolean(expandedDateGroups[group.key])}
                  onChange={(_, expanded) =>
                    setExpandedDateGroups((prev) => ({
                      ...prev,
                      [group.key]: expanded,
                    }))
                  }
                  sx={{
                    mb: 1.1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "12px !important",
                    overflow: "hidden",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700 }}>{group.label}</Typography>
                    <Chip
                      size="small"
                      label={`${group.reports.length} report${
                        group.reports.length === 1 ? "" : "s"
                      }`}
                      sx={{ ml: 1 }}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    {group.reports.map((report, reportIndex) => {
                      const localeEntries = report.locales.map((locale) => ({
                        locale,
                        note: String(report.releaseNotesByLocale[locale] || "").trim(),
                      }))
                      .filter((entry) =>
                        GOOGLE_PLAY_LOCALE_CODE_SET.has(entry.locale.toLowerCase()),
                      );
                      const allNotesText = localeEntries
                        .map(
                          (entry) =>
                            `<${entry.locale}>\n${entry.note}\n</${entry.locale}>`,
                        )
                        .join("\n\n");

                      return (
                        <Card
                          key={`${report.reportId}_${reportIndex}`}
                          variant="outlined"
                          sx={{ mb: 1.2, borderRadius: 2 }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  {report.templateLabel}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Generated {formatDateTime(report.generatedAt)} •{" "}
                                  {report.locales.length} locale
                                  {report.locales.length === 1 ? "" : "s"} •{" "}
                                  {report.modelUsed}
                                </Typography>
                              </Box>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ContentCopyIcon fontSize="small" />}
                                onClick={() =>
                                  copyText(`all_${report.reportId}`, allNotesText)
                                }
                              >
                                {copiedKey === `all_${report.reportId}`
                                  ? "Copied"
                                  : "Copy all"}
                              </Button>
                            </Box>
                            <Divider sx={{ mb: 1.2 }} />
                            <Grid container spacing={1.2}>
                              {localeEntries.map((entry) => (
                                <Grid item xs={12} md={6} key={`${report.reportId}_${entry.locale}`}>
                                  <Box
                                    sx={{
                                      border: "1px solid",
                                      borderColor: "divider",
                                      borderRadius: 1.5,
                                      p: 1.25,
                                      bgcolor: "rgba(255,255,255,0.7)",
                                      height: "100%",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 0.75,
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                        {getStoreLocaleDisplayLabel(entry.locale)}
                                      </Typography>
                                      <Button
                                        size="small"
                                        variant="text"
                                        startIcon={<ContentCopyIcon fontSize="small" />}
                                        onClick={() =>
                                          copyText(
                                            `${report.reportId}_${entry.locale}`,
                                            entry.note,
                                          )
                                        }
                                      >
                                        {copiedKey === `${report.reportId}_${entry.locale}`
                                          ? "Copied"
                                          : "Copy"}
                                      </Button>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}
                                    >
                                      {entry.note}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default GooglePlayReleaseNotesPage;
